/**
 * Tough-Bar Builder - API Module
 * API communication and product data fetching methods
 *
 * API uses context-driven architecture:
 * - Context = user's choices (input sent TO the API)
 * - Kit = computed products/images (output returned FROM the API)
 */
window.ToughBarBuilder = window.ToughBarBuilder || {};

const TOUGH_BAR_BUILDER_ENDPOINT = 'tough-bar-builder-v1';
const TOUGH_BAR_SNAPSHOT_ENDPOINT = 'tough-bar-builder-configuration-snapshots-v1';
const TOUGH_BAR_PROXY_PATH = '/apps/tough-bar';

window.ToughBarBuilder.api = {
  isPlainBuilderContextObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  },

  cloneBuilderContextValue(value) {
    if (Array.isArray(value)) {
      return value.map(entry => this.cloneBuilderContextValue(entry));
    }

    if (this.isPlainBuilderContextObject(value)) {
      return { ...value };
    }

    return value;
  },

  getBuilderEndpoint() {
    return TOUGH_BAR_BUILDER_ENDPOINT;
  },

  getBuilderSnapshotEndpoint() {
    return TOUGH_BAR_SNAPSHOT_ENDPOINT;
  },

  getBuilderProxyPath() {
    return TOUGH_BAR_PROXY_PATH;
  },

  buildBuilderProxyUrl(targetEndpoint) {
    const endpoint = String(targetEndpoint || '').trim();
    if (!endpoint) return this.getBuilderProxyPath();

    try {
      const proxyUrl = new URL(this.getBuilderProxyPath(), window.location.origin);
      proxyUrl.searchParams.set('target', endpoint);
      return `${proxyUrl.pathname}${proxyUrl.search}`;
    } catch (_error) {
      const separator = this.getBuilderProxyPath().includes('?') ? '&' : '?';
      return `${this.getBuilderProxyPath()}${separator}target=${encodeURIComponent(endpoint)}`;
    }
  },

  normalizeBuilderContextShape(context = {}) {
    if (!context || typeof context !== 'object') {
      return {};
    }

    const nextContext = { ...context };
    const tabletContext = (context.tablet && typeof context.tablet === 'object' && !Array.isArray(context.tablet))
      ? context.tablet
      : null;

    if (!tabletContext) {
      return nextContext;
    }

    if (nextContext.tablet_make === undefined && tabletContext.make !== undefined) {
      nextContext.tablet_make = tabletContext.make;
    }
    if (nextContext.tablet_model === undefined && tabletContext.model !== undefined) {
      nextContext.tablet_model = tabletContext.model;
    }
    if (nextContext.tablet_case_type === undefined && tabletContext.case_type !== undefined) {
      nextContext.tablet_case_type = tabletContext.case_type;
    }

    if (nextContext.tablet_mount === undefined && tabletContext.mount && typeof tabletContext.mount === 'object') {
      nextContext.tablet_mount = { ...tabletContext.mount };
    }
    if (nextContext.tablet_case === undefined && tabletContext.case && typeof tabletContext.case === 'object') {
      nextContext.tablet_case = { ...tabletContext.case };
    }
    if (nextContext.tablet_dock === undefined && tabletContext.dock && typeof tabletContext.dock === 'object') {
      nextContext.tablet_dock = { ...tabletContext.dock };
    }
    if (nextContext.tablet_keyboard === undefined && tabletContext.keyboard && typeof tabletContext.keyboard === 'object') {
      nextContext.tablet_keyboard = { ...tabletContext.keyboard };
    }

    return nextContext;
  },

  mergeBuilderContextValue(baseValue, incomingValue, options = {}) {
    const { overwrite = false } = options;

    if (incomingValue === undefined) {
      return overwrite ? undefined : this.cloneBuilderContextValue(baseValue);
    }

    if (incomingValue === null) {
      return null;
    }

    if (overwrite || !this.isPlainBuilderContextObject(baseValue) || !this.isPlainBuilderContextObject(incomingValue)) {
      return this.cloneBuilderContextValue(incomingValue);
    }

    return {
      ...baseValue,
      ...incomingValue
    };
  },

  mergeBuilderContexts(baseContext = {}, incomingContext = {}, options = {}) {
    const { overwrite = false } = options;

    if (overwrite) {
      return this.normalizeBuilderContextShape(incomingContext);
    }

    const nextContext = {
      ...(baseContext && typeof baseContext === 'object' ? baseContext : {}),
      ...(incomingContext && typeof incomingContext === 'object' ? incomingContext : {})
    };

    ['tablet_mount', 'tablet_case', 'tablet_dock', 'tablet_keyboard'].forEach(key => {
      const mergedValue = this.mergeBuilderContextValue(baseContext?.[key], incomingContext?.[key], options);
      if (mergedValue === undefined) {
        delete nextContext[key];
        return;
      }
      nextContext[key] = mergedValue;
    });

    if (incomingContext?.tablet === null) {
      nextContext.tablet = null;
      return this.normalizeBuilderContextShape(nextContext);
    }

    if (this.isPlainBuilderContextObject(baseContext?.tablet) || this.isPlainBuilderContextObject(incomingContext?.tablet)) {
      const baseTablet = this.isPlainBuilderContextObject(baseContext?.tablet) ? baseContext.tablet : {};
      const incomingTablet = this.isPlainBuilderContextObject(incomingContext?.tablet) ? incomingContext.tablet : {};
      const mergedTablet = {
        ...baseTablet,
        ...incomingTablet
      };

      ['mount', 'case', 'dock', 'keyboard'].forEach(key => {
        const mergedValue = this.mergeBuilderContextValue(baseTablet?.[key], incomingTablet?.[key], options);
        if (mergedValue === undefined) {
          delete mergedTablet[key];
          return;
        }
        mergedTablet[key] = mergedValue;
      });

      nextContext.tablet = mergedTablet;
    }

    return this.normalizeBuilderContextShape(nextContext);
  },

  getResponseAdapter() {
    return window.ToughBarBuilder.responseAdapter || null;
  },

  nextBuilderApiRequestId() {
    const next = Number(this._builderApiRequestSeq || 0) + 1;
    this._builderApiRequestSeq = next;
    return next;
  },

  isLatestBuilderApiRequest(requestId) {
    return Number(requestId || 0) === Number(this._builderApiRequestSeq || 0);
  },

  async api(additionalData = {}) {
    const requestId = this.nextBuilderApiRequestId();
    const isSnapshotRestore = Boolean(additionalData?.load_from_snapshot_id);
    this.loading = true;
    this.error = null;

    try {
      if (isSnapshotRestore) {
        this.accessoryButtonsCache = [];
      }

      // Build context with nested tablet structure if tablet is configured
      const body = this.buildApiContext(additionalData);

      const rawResponse = await this.postToBuilder(this.getBuilderEndpoint(), body);
      if (!this.isLatestBuilderApiRequest(requestId)) return;
      const responseAdapter = this.getResponseAdapter();
      if (!responseAdapter || typeof responseAdapter.adaptBuilderResponse !== 'function') {
        throw new Error('Builder response adapter is unavailable.');
      }

      // Fail fast on contract drift before mutating UI state.
      this.response = responseAdapter.adaptBuilderResponse(rawResponse);

      // Keep local context aligned with backend-normalized context (critical for snapshot/share restore).
      if (this.response?.context && typeof this.response.context === 'object') {
        this.context = this.mergeBuilderContexts(this.context, this.response.context, {
          overwrite: isSnapshotRestore
        });
      } else if (isSnapshotRestore) {
        this.context = {};
      }

      if (typeof this.syncDeviceStepState === 'function') {
        this.syncDeviceStepState();
      }

      const responseMarksAccessoriesComplete = this.response?.context?.accessories_complete === true;
      const isFinalKitState = Boolean(
        this.isFinalKitState
        || responseMarksAccessoriesComplete
      );
      if (typeof this.setAccessoriesComplete === 'function') {
        this.setAccessoriesComplete(isFinalKitState);
      } else if (isFinalKitState) {
        this.context.accessories_complete = true;
      } else {
        delete this.context.accessories_complete;
      }

      const tabletButton = this.response.section?.buttons?.find(button => button.id === 'tablet');
      if (tabletButton?.modal?.options) {
        this.tabletOptionsCache = tabletButton.modal.options;
      }

      const sectionButtons = Array.isArray(this.response.section?.buttons) ? this.response.section.buttons : [];
      const stepName = String(this.response?.step_name || '').trim().toLowerCase();
      const isAccessoryStep = Number(this.response?.step) === 3 || stepName === 'accessories';
      if (isAccessoryStep && sectionButtons.length > 0) {
        this.accessoryButtonsCache = sectionButtons;
      }

      if (isAccessoryStep && typeof this.ensureKeyboardAccessoryCustomizationReady === 'function') {
        await this.ensureKeyboardAccessoryCustomizationReady();
      }

      // Fetch product data for all products in kit (including tough_bar)
      await this.fetchKitProductData();
      if (!this.isLatestBuilderApiRequest(requestId)) return;

      if (isSnapshotRestore && this.step < 3) {
        this.selectedAccessories = [];
        if (typeof this.clearAccessoryCustomizeAvailability === 'function') {
          this.clearAccessoryCustomizeAvailability();
        }
      }

      // Rebuild local accessory state from Step 3 response payloads (critical for shared URL restore).
      if (isAccessoryStep && typeof this.hydrateAccessoriesFromStepButtons === 'function') {
        this.hydrateAccessoriesFromStepButtons({ overwrite: isSnapshotRestore });
      }

      // Keep accessory selection state in sync when API returns final kit payloads.
      const hasSummaryKits = Array.isArray(this.response?.kits) && this.response.kits.length > 0;
      if (isFinalKitState && hasSummaryKits && typeof this.hydrateAccessoriesFromSummaryKits === 'function') {
        this.hydrateAccessoriesFromSummaryKits({ overwrite: isSnapshotRestore });
      }

      if (typeof this.restorePersistedCartStateFromContext === 'function') {
        this.restorePersistedCartStateFromContext(this.context);
      }

    } catch (err) {
      if (!this.isLatestBuilderApiRequest(requestId)) return;
      console.error('Tough-Bar API Error:', err);
      if (err?.name === 'ToughBarApiContractError') {
        this.error = `API contract error: ${err.message}`;
      } else {
        this.error = `API Error: ${err.message}`;
      }
    } finally {
      if (this.isLatestBuilderApiRequest(requestId)) {
        this.loading = false;
      }
    }
  },

  async postToBuilder(targetEndpoint, body = {}) {
    const sendRequest = async endpoint => fetch(this.buildBuilderProxyUrl(endpoint), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Target-Endpoint': endpoint
      },
      body: JSON.stringify(body)
    });

    let endpoint = targetEndpoint;
    let res = await sendRequest(endpoint);

    const legacyEndpoint = String(targetEndpoint || '').replace(/-v\d+$/, '');
    const canFallback = res.status === 404 && legacyEndpoint && legacyEndpoint !== targetEndpoint;
    if (canFallback) {
      endpoint = legacyEndpoint;
      res = await sendRequest(endpoint);
    }

    if (!res.ok) {
      let message = `API error: ${res.status}`;
      try {
        const errorData = await res.json();
        message = errorData?.error || errorData?.details || message;
      } catch (_parseError) {
        // Fall back to status-based message when response is not JSON.
      }
      throw new Error(message);
    }

    return res.json();
  },

  buildSnapshotConfiguration() {
    if (this.step < 2) return null;
    return this.buildApiContext({ current_step: this.step || 1 });
  },

  async createSnapshot(configuration = null) {
    const payload = configuration || this.buildSnapshotConfiguration();
    if (!payload) return null;
    return this.postToBuilder(this.getBuilderSnapshotEndpoint(), payload);
  },

  async loadSnapshot(snapshotId) {
    if (!snapshotId) return false;
    await this.api({ load_from_snapshot_id: snapshotId });
    return !this.error;
  },

  buildApiContext(additionalData = {}) {
    const body = { ...additionalData };

    if (additionalData?.load_from_snapshot_id) {
      return body;
    }

    // Add vehicle context
    if (this.context.year) body.year = this.context.year;
    if (this.context.make) body.make = this.context.make;
    if (this.context.model) body.model = this.context.model;
    if (this.context.trim_level) body.trim_level = this.context.trim_level;

    // Add flat tablet fields (required for step transitions)
    if (this.context.tablet_make) body.tablet_make = this.context.tablet_make;
    if (this.context.tablet_model) body.tablet_model = this.context.tablet_model;
    if (this.context.tablet_case_type) body.tablet_case_type = this.context.tablet_case_type;

    // Build nested tablet structure for customization
    if (this.context.tablet_make || this.context.tablet_model || this.context.tablet_case_type) {
      body.tablet = {
        make: this.context.tablet_make,
        model: this.context.tablet_model,
        case_type: this.context.tablet_case_type
      };

      // Include any customization choices
      if (this.context.tablet_mount) body.tablet.mount = this.context.tablet_mount;
      if (this.context.tablet_case) body.tablet.case = this.context.tablet_case;
      if (this.context.tablet_dock) body.tablet.dock = this.context.tablet_dock;
      if (this.context.tablet_keyboard) body.tablet.keyboard = this.context.tablet_keyboard;
    }

    // Add laptop/other device if selected
    if (this.context.laptop_selected) body.laptop_selected = true;
    if (this.context.laptop_style) body.laptop_style = this.context.laptop_style;
    if (this.context.other_device_selected) body.other_device_selected = true;

    const cartPersistenceScope = typeof this.getActiveCartPersistenceScope === 'function'
      ? this.getActiveCartPersistenceScope()
      : null;
    body.cart_quantities = typeof this.normalizePersistedCartQuantities === 'function'
      ? this.normalizePersistedCartQuantities(this.quantities, { scope: cartPersistenceScope })
      : {};
    body.excluded_products = typeof this.normalizePersistedExcludedProducts === 'function'
      ? this.normalizePersistedExcludedProducts(this.excludedProducts, { scope: cartPersistenceScope })
      : [];

    const targetStep = Number(additionalData.target_step);
    const currentStep = Number(additionalData.current_step || this.step || 1);
    const isGoingBackBeforeAccessories = additionalData.go_back && Number.isFinite(targetStep) && targetStep < 3;
    const shouldPersistFinalKitState = !isGoingBackBeforeAccessories
      && (!Number.isFinite(currentStep) || currentStep >= 3)
      && Boolean(this.isFinalKitState || this.context.accessories_complete === true);

    if (shouldPersistFinalKitState) {
      body.accessories_complete = true;
    }

    // Add accessories context
    if (this.shouldIncludeAccessories(additionalData)) {
      body.accessories = this.buildAccessoriesContextPayload();
    }

    return body;
  },

  buildAccessoriesContextPayload() {
    const accessories = {};
    const normalizeId = value => {
      if (typeof this.normalizeAccessoryId === 'function') {
        return this.normalizeAccessoryId(value);
      }
      return String(value || '').trim().replace(/___+/g, '_');
    };
    const isMatch = (left, right) => normalizeId(left) === normalizeId(right);

    const addAccessory = (rawId, config) => {
      const accessoryId = String(rawId || '').trim();
      if (!accessoryId || !config || typeof config !== 'object') return;

      const existingKey = Object.keys(accessories).find(key => isMatch(key, accessoryId));
      const targetKey = existingKey || accessoryId;
      accessories[targetKey] = {
        ...(accessories[targetKey] || {}),
        ...config
      };
    };

    if (Array.isArray(this.selectedAccessories) && this.selectedAccessories.length > 0) {
      this.selectedAccessories.forEach(accessory => {
        if (typeof this.isKeyboardAccessoryButton === 'function' && this.isKeyboardAccessoryButton(accessory?.id)) {
          return;
        }
        addAccessory(accessory?.id, accessory?.config || {});
      });
    }

    return accessories;
  },

  shouldIncludeAccessories(additionalData = {}) {
    const targetStep = Number(additionalData.target_step);
    if (additionalData.go_back && Number.isFinite(targetStep) && targetStep < 3) {
      return false;
    }

    const currentStep = Number(additionalData.current_step);
    if (Number.isFinite(currentStep)) {
      return currentStep >= 3;
    }

    if (this.step >= 3) {
      return true;
    }

    return this.selectedAccessories && this.selectedAccessories.length > 0;
  },

  async fetchProductData(handle) {
    if (!handle || this.products[handle]) return;

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (res.ok) {
        const product = await res.json();
        this.products[handle] = product;
      }
    } catch (err) {
      console.error(`Failed to fetch product ${handle}:`, err);
    }
  },

  async fetchKitProductData() {
    // Fetch product data for all products in response.kit
    const kit = this.response.kit || {};
    const handlesToFetch = new Set();
    const addHandles = (handles = []) => {
      if (!Array.isArray(handles)) return;
      handles.forEach(handle => {
        if (!handle || typeof handle !== 'string') return;
        handlesToFetch.add(handle);
      });
    };

    // Fetch tough bar products
    addHandles(kit.tough_bar?.products);

    // Fetch device kit products (tablet, laptop, other_device)
    const deviceTypes = ['tablet', 'laptop', 'other_device'];
    for (const deviceType of deviceTypes) {
      const deviceKit = kit[deviceType];
      addHandles(deviceKit?.products);
    }

    // Fetch accessory kit products when provided by API
    addHandles(kit.accessories?.products);

    // Also load per-kit products when provided by API.
    if (Array.isArray(this.response.kits)) {
      this.response.kits.forEach(summaryKit => {
        addHandles(summaryKit?.products);
      });
    }

    if (handlesToFetch.size > 0) {
      await Promise.all(Array.from(handlesToFetch).map(handle => this.fetchProductData(handle)));
    }
  },
};
